import queryString from 'querystring'

import { SortDirection } from '@amzn/stencil-react-components/table'

import { APP_CONFIG } from 'src/config.app'
import { Locale } from 'src/models/dto/Locale'
import { DisplayType, OrderBy, QueryParams, SearchQuery } from './types'

export const defaultPageSize = 10

/**
 * Converts URLSearchParams to SearchQuery
 */
export function fromParams(
    searchParams: URLSearchParams,
    options: {
        lockedFileTypes?: string[]
        pageSize?: number
        displayType: DisplayType
    }
): SearchQuery {
    const pageIndexSearchParam = searchParams.get(QueryParams.PAGE_INDEX) ?? undefined
    const numberPageIndex = pageIndexSearchParam ? +pageIndexSearchParam : 0

    const orderBy = (searchParams.get(QueryParams.ORDER_BY) as OrderBy) ?? OrderBy.TITLE
    const orderDirection =
        (searchParams.get(QueryParams.ORDER_DIRECTION) as SortDirection) ?? SortDirection.Ascending

    const searchQuery: SearchQuery = {
        search: searchParams.get(QueryParams.SEARCH) ?? '',
        pageIndex: numberPageIndex,
        createdBy: searchParams.get(QueryParams.CREATED_BY) ?? undefined,
        createdAfterOrAt: searchParams.get(QueryParams.CREATED_AFTER_OR_AT) ?? undefined,
        createdBeforeOrAt: searchParams.get(QueryParams.CREATED_BEFORE_OR_AT) ?? undefined,
        defaultFileName: searchParams.get(QueryParams.DEFAULT_FILENAME) ?? undefined,
        fileType: options.lockedFileTypes ?? searchParams.getAll(QueryParams.FILE_TYPE) ?? [],
        locale: (searchParams.getAll(QueryParams.LOCALE) as Locale[]) ?? [],
        pageSize: options.pageSize ?? defaultPageSize,
        orderBy,
        orderDirection,
    }

    const englishPublished = searchParams.get(QueryParams.ENGLISH_PUBLISHED)
    if (englishPublished === 'true' || options.displayType === DisplayType.ASSIGN_TO_MODULE_PAGE) {
        searchQuery.englishPublished = true
    }

    return searchQuery
}

/**
 * Converts SearchQuery to URLSearchParams
 */
export function toSearchParams(
    searchQuery: SearchQuery,
    options: {
        lockedFileTypes?: string[]
        displayType?: DisplayType
        isNewSearch?: boolean
    }
): URLSearchParams {
    const queryParams: string[][] = [
        [QueryParams.SEARCH, searchQuery.search],
        [QueryParams.PAGE_INDEX, String(options.isNewSearch ? 0 : searchQuery.pageIndex)],
        [QueryParams.PAGE_SIZE, String(searchQuery.pageSize ?? defaultPageSize)],
        [QueryParams.ORDER_BY, searchQuery.orderBy ?? OrderBy.TITLE],
        [QueryParams.ORDER_DIRECTION, searchQuery.orderDirection ?? SortDirection.Ascending],
    ]

    if (searchQuery.createdAfterOrAt) {
        queryParams.push([QueryParams.CREATED_AFTER_OR_AT, searchQuery.createdAfterOrAt])
    }

    if (searchQuery.createdBeforeOrAt) {
        queryParams.push([QueryParams.CREATED_BEFORE_OR_AT, searchQuery.createdBeforeOrAt])
    }

    if (searchQuery.createdBy) {
        queryParams.push([QueryParams.CREATED_BY, searchQuery.createdBy])
    }

    if (searchQuery.defaultFileName) {
        queryParams.push([QueryParams.DEFAULT_FILENAME, searchQuery.defaultFileName])
    }

    if (options.lockedFileTypes) {
        options.lockedFileTypes.forEach((f) => {
            queryParams.push([QueryParams.FILE_TYPE, f])
        })
    } else if (searchQuery.fileType && searchQuery.fileType.length > 0) {
        searchQuery.fileType.forEach((f) => {
            queryParams.push([QueryParams.FILE_TYPE, f])
        })
    }

    if (searchQuery.locale && searchQuery.locale.length > 0) {
        searchQuery.locale.forEach((l) => {
            queryParams.push([QueryParams.LOCALE, l])
        })
    }

    if (
        searchQuery.englishPublished === true ||
        options.displayType === DisplayType.ASSIGN_TO_MODULE_PAGE
    ) {
        queryParams.push([QueryParams.ENGLISH_PUBLISHED, 'true'])
    }

    return new URLSearchParams(queryParams)
}

// Does not depend on nextSearchQuery
export const getSearchRequest = (searchQuery: SearchQuery) => {
    const { fileType, locale, orderBy, orderDirection, ...nonArrayArguments } = searchQuery
    const arrayArguments = queryString.stringify({
        ...(fileType ? { fileType: fileType } : {}),
        ...(locale ? { locale: locale } : {}),
    })
    return {
        method: 'GET' as const,
        url:
            `${APP_CONFIG.backendAPIV1BaseUrl}/media-manager/media?` +
            (arrayArguments !== '&' ? arrayArguments : ''),
        params: {
            ...nonArrayArguments,
            [QueryParams.PAGE_SIZE]: String(searchQuery.pageSize ?? defaultPageSize),
            [QueryParams.ORDER_BY]: orderBy,
            [QueryParams.ORDER_DIRECTION]: orderDirection,
        },
    }
}
